import { useEffect, useRef, useState } from "react";
import api, { resolveImageUrl, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Sparkles, X, Wand2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const baseBtn = "inline-flex items-center justify-center gap-1 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em] border cursor-pointer transition-colors disabled:opacity-40 disabled:cursor-not-allowed";

/**
 * Floating popover that lets the curator drop an AI-generated backdrop behind
 * the canvas. Hits POST /api/images/generate-backdrop (Gemini Nano Banana).
 *
 * Loading UX is deliberately verbose — the model takes 8–25s, so we surface a
 * progress bar, an elapsed timer, and a clear "this is normal" hint.
 *
 * Props:
 *   - open: bool
 *   - currentUrl: string | "" (active backdrop)
 *   - onClose()
 *   - onApply(url|"")  — "" clears the backdrop
 */
export default function BackdropPopover({ open, currentUrl, onClose, onApply }) {
  const [presets, setPresets] = useState([]);
  const [busy, setBusy] = useState(false);
  const [busyKey, setBusyKey] = useState("");
  const [progress, setProgress] = useState(0); // 0..100 fake easing for ux
  const [elapsed, setElapsed] = useState(0);
  const [prompt, setPrompt] = useState("");
  const [lastError, setLastError] = useState(""); // surface backend errors inline
  const tickRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    api.get("/images/backdrop-presets").then(({ data }) => setPresets(data)).catch(() => {});
  }, [open]);

  // Tick a faux progress bar while the request is in flight
  useEffect(() => {
    if (!busy) {
      if (tickRef.current) clearInterval(tickRef.current);
      tickRef.current = null;
      return;
    }
    setProgress(0); setElapsed(0);
    const t0 = Date.now();
    tickRef.current = setInterval(() => {
      const ms = Date.now() - t0;
      setElapsed(Math.round(ms / 100) / 10);
      // Asymptote at ~92% so we never look "done" before we actually are
      const pct = 92 * (1 - Math.exp(-ms / 9000));
      setProgress(Math.min(92, Math.round(pct)));
    }, 250);
    return () => { if (tickRef.current) clearInterval(tickRef.current); tickRef.current = null; };
  }, [busy]);

  if (!open) return null;

  const generate = async (preset, customPrompt) => {
    setBusy(true);
    setBusyKey(preset || "custom");
    setLastError("");
    toast.loading("Generating backdrop with Nano Banana…", { id: "backdrop" });
    try {
      const { data } = await api.post(
        "/images/generate-backdrop",
        { preset: preset || "", prompt: customPrompt || "" },
        { timeout: 120000 } // 120s — Nano Banana cold-starts can be slow
      );
      // Preload the result before flipping state — guarantees the canvas
      // never paints a half-loaded URL and the "applied" feels instant.
      const fullUrl = data.image_url.startsWith("http")
        ? data.image_url
        : `${process.env.REACT_APP_BACKEND_URL || ""}${data.image_url}`;
      await new Promise((resolve) => {
        const img = new Image();
        img.onload = resolve;
        img.onerror = resolve; // fall through anyway after 8s timeout
        img.src = fullUrl;
        setTimeout(resolve, 8000);
      });
      setProgress(100);
      onApply?.(data.image_url);
      toast.success(data.attempts > 1 ? "Backdrop applied (after retry)" : "Backdrop applied", { id: "backdrop" });
      onClose?.();
    } catch (e) {
      const msg = formatApiError(e) || "Backdrop generation failed";
      const friendly = /timeout|aborted/i.test(msg)
        ? "Nano Banana timed out — try again or pick a different preset."
        : /502|generation failed/i.test(msg)
          ? "Nano Banana is busy. Try this preset again, or pick another."
          : msg;
      setLastError(friendly);
      toast.error(friendly, { id: "backdrop", duration: 5000 });
    } finally {
      setBusy(false);
      setBusyKey("");
    }
  };

  return (
    <div
      data-testid="backdrop-popover"
      className="absolute top-4 right-4 z-30 w-80 bg-white border border-[#333333] shadow-lg p-4"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-1.5">
          <Sparkles className="w-3.5 h-3.5 text-[#9CA47E]" />
          <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#333333]">AI backdrop</span>
        </div>
        <button data-testid="backdrop-close" type="button" onClick={onClose} className="text-[#595959] hover:text-[#333333] cursor-pointer" disabled={busy}>
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Loading panel — replaces the picker while generation is in flight */}
      {busy ? (
        <div data-testid="backdrop-loading" className="py-2">
          <div className="flex items-center gap-2 mb-3">
            <Loader2 className="w-4 h-4 text-[#9CA47E] animate-spin" />
            <div className="flex-1">
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#333333]">
                {busyKey === "custom" ? "Painting your prompt" : `Painting · ${busyKey.replace(/_/g, " ")}`}
              </div>
              <div className="font-mono text-[9px] text-[#595959] mt-0.5">{elapsed.toFixed(1)}s elapsed · usually 8–25s</div>
            </div>
          </div>
          <div className="h-1 bg-[#F2EDE3] overflow-hidden">
            <div
              data-testid="backdrop-progress"
              className="h-full bg-[#9CA47E] transition-[width] duration-200"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-[#A0A0A0] mt-3 leading-relaxed">
            ◇ Gemini Nano Banana is composing your backdrop. The first request after a quiet period can take a little longer — keep this open.
          </div>
        </div>
      ) : (
        <>
          <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#595959] mb-2">Pick a preset</div>
          <div className="grid grid-cols-2 gap-1.5 mb-3">
            {presets.map((p) => (
              <button
                key={p.key}
                type="button"
                data-testid={`backdrop-preset-${p.key}`}
                onClick={() => generate(p.key, "")}
                className={`${baseBtn} border-[#E5E5E5] text-[#333333] hover:border-[#9CA47E] hover:text-[#9CA47E] py-2`}
              >
                {p.label}
              </button>
            ))}
          </div>

          <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#595959] mb-2">Or describe one</div>
          <div className="flex gap-1.5">
            <Input
              data-testid="backdrop-prompt"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && prompt.trim()) generate("", prompt); }}
              placeholder="e.g. dusty pink plaster wall"
              className="rounded-none border-[#E5E5E5] focus-visible:ring-0 focus-visible:border-[#333333] text-xs h-8 flex-1"
            />
            <button
              type="button"
              data-testid="backdrop-generate"
              disabled={!prompt.trim()}
              onClick={() => generate("", prompt)}
              className={`${baseBtn} bg-[#9CA47E] text-white border-[#9CA47E] hover:bg-[#7E876A]`}
            >
              <Wand2 className="w-3 h-3" /> Make
            </button>
          </div>

          {currentUrl && (
            <div className="mt-3 pt-3 border-t border-[#EFEFEF] flex items-center gap-2">
              <img src={resolveImageUrl(currentUrl)} alt="" className="w-10 h-10 object-cover border border-[#E5E5E5]" />
              <div className="flex-1 font-mono text-[9px] uppercase tracking-[0.2em] text-[#595959]">Current backdrop</div>
              <button
                type="button"
                data-testid="backdrop-clear"
                onClick={() => { onApply?.(""); onClose?.(); }}
                className={`${baseBtn} border-[#E5E5E5] text-[#595959] hover:border-[#9CA47E] hover:text-[#9CA47E]`}
              >
                Clear
              </button>
            </div>
          )}

          {lastError && (
            <div data-testid="backdrop-error" className="mt-3 px-2 py-2 border border-[#9CA47E] bg-[#FFF5F4] font-mono text-[10px] uppercase tracking-[0.18em] text-[#9CA47E] leading-relaxed">
              ⚠ {lastError}
            </div>
          )}

          <div className="mt-3 font-mono text-[8px] uppercase tracking-[0.22em] text-[#A0A0A0] leading-relaxed">
            Generated by Gemini Nano Banana · 8–25s · saved to your set · auto-retried once on failure
          </div>
        </>
      )}
    </div>
  );
}
